import { z } from "zod";

/** Product entitlement feature keys — do not invent unrelated keys. */
export const PRODUCT_MODEL_SLUGS = [
  "bee",
  "horse",
  "cow",
  "pig",
  "cat",
  "dog",
  "rat",
  "fish",
  "chicken",
] as const;

export type ProductModelSlug = (typeof PRODUCT_MODEL_SLUGS)[number];

export const modelFeatureKey = (slug: string): `model:${string}` =>
  `model:${slug.trim().toLowerCase()}`;

export const CATALOG_FEATURE_KEY = "catalog" as const;
export const EXAM_MODE_FEATURE_KEY = "exam_mode" as const;

export const PRODUCT_FEATURE_KEYS = [
  ...PRODUCT_MODEL_SLUGS.map((slug) => modelFeatureKey(slug)),
  CATALOG_FEATURE_KEY,
  EXAM_MODE_FEATURE_KEY,
] as const;

export type ProductFeatureKey = (typeof PRODUCT_FEATURE_KEYS)[number];

export const productFeatureKeySchema = z.enum(
  PRODUCT_FEATURE_KEYS as unknown as [ProductFeatureKey, ...ProductFeatureKey[]],
);

export const isProductFeatureKey = (value: string): value is ProductFeatureKey =>
  (PRODUCT_FEATURE_KEYS as readonly string[]).includes(value);

export const isPremiumModelSlug = (slug: string): boolean =>
  (PRODUCT_MODEL_SLUGS as readonly string[]).includes(
    slug.trim().toLowerCase() as ProductModelSlug,
  );

export const ALL_PRODUCT_FEATURES: readonly ProductFeatureKey[] = [
  ...PRODUCT_FEATURE_KEYS,
];

export const SUBSCRIPTION_PAID_FEATURES: readonly ProductFeatureKey[] = [
  ...PRODUCT_MODEL_SLUGS.map((slug) => modelFeatureKey(slug)),
  CATALOG_FEATURE_KEY,
];

export const SUBSCRIPTION_TRIAL_FEATURES: readonly ProductFeatureKey[] = [
  modelFeatureKey("horse"),
  CATALOG_FEATURE_KEY,
];

export const FREE_TRIAL_LOOKUP_KEY = "7days_free_trial";

/** Derived client entitlement DTO (no grandfather marker). */
export const productEntitlementsDtoSchema = z.object({
  productFeatures: z.array(z.string()),
  examMode: z.boolean(),
});

export type ProductEntitlementsDto = z.infer<typeof productEntitlementsDtoSchema>;
